// 2026-09-24 - FormuLearning - Step 4 Next Optimization «계산 제외» 선택 (이슈 #76 G5, M901-29)
// 파일 위치: frontend/src/features/step4/GridExclusionPicker.tsx
// 목적: 이번 run 의 계산에서 뺄 **시점·용출액**을 기준 대조약 Lot 격자에서 고른다(칩 토글).
//       삭제가 아니라 계산 제외다 — 실험 결과는 그대로 두고 이 optimization 이 쓸 값만 골라 계산한다.
//       초기값은 직전 optimization round 의 제외다(Q-260902-07 B, 사용자 260924) — 뺀 이유가 «용출이
//       충분해서» 이므로 이어서 빼되, 여기서 추가·수정·해제할 수 있다. 서버도 같은 규칙으로 검증한다
//       (남는 용출액 ≥ 1 · 남는 시점 ≥ 3, 422 `grid_exclusion_invalid`).

import type { GridExclusion } from './gridExclusion'
import { exclusionProblem, hasExclusion, solutionKey } from './gridExclusion'

interface Props {
  /** 기준 대조약 Lot 격자. 비어 있으면(대조약 미입력) 고를 것이 없다. */
  grid: { time_points: number[]; solutions: string[] } | null
  value: GridExclusion
  onChange: (next: GridExclusion) => void
  /** 직전 optimization round 에서 이어받은 값과 같은가 — «직전 라운드 설정» 표기. */
  carried: boolean
  disabled?: boolean
}

function Chip({ label, on, onClick, disabled, testId }: {
  label: string; on: boolean; onClick: () => void; disabled?: boolean; testId: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      data-testid={testId}
      title={on ? '계산 제외 — 눌러서 다시 포함' : '계산 포함 — 눌러서 제외'}
      className="rounded border px-1.5 py-0.5 text-[10px] font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
      style={on
        ? { borderColor: 'var(--color-warn)', color: 'var(--color-text-muted)', textDecoration: 'line-through' }
        : { borderColor: 'var(--border-soft)', color: 'var(--color-text-body)' }}
    >
      {label}
    </button>
  )
}

export default function GridExclusionPicker({ grid, value, onChange, carried, disabled }: Props) {
  if (!grid || (grid.time_points.length === 0 && grid.solutions.length === 0)) return null
  const sols = new Set(value.excluded_solutions.map(solutionKey))
  const times = new Set(value.excluded_time_points.map(Number))

  const toggleTime = (t: number) => {
    const next = times.has(Number(t))
      ? value.excluded_time_points.filter((x) => Number(x) !== Number(t))
      : [...value.excluded_time_points, t]
    onChange({ ...value, excluded_time_points: grid.time_points.filter((x) => next.some((y) => Number(y) === Number(x))) })
  }
  const toggleSol = (s: string) => {
    const k = solutionKey(s)
    const next = sols.has(k)
      ? value.excluded_solutions.filter((x) => solutionKey(x) !== k)
      : [...value.excluded_solutions, s]
    onChange({ ...value, excluded_solutions: grid.solutions.filter((x) => next.some((y) => solutionKey(y) === solutionKey(x))) })
  }
  const problem = exclusionProblem(grid, value)

  return (
    <div className="mb-3" data-testid="grid-exclusion-picker">
      <div className="flex flex-wrap items-center gap-2">
        <h5 className="text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
          계산 제외
        </h5>
        {hasExclusion(value) && carried && (
          <span className="rounded px-1.5 py-0.5 text-[10px]"
                style={{ background: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }}
                data-testid="grid-exclusion-carried">
            직전 라운드 설정
          </span>
        )}
        {hasExclusion(value) && !disabled && (
          <button
            type="button"
            onClick={() => onChange({ excluded_time_points: [], excluded_solutions: [] })}
            className="text-[10px] text-text-secondary underline"
            data-testid="grid-exclusion-clear"
          >
            모두 포함
          </button>
        )}
      </div>
      <p className="mt-1 text-[10px] text-text-muted">
        용출이 충분해 더 볼 필요가 없는 시점·용출액을 이번 계산에서 뺍니다. 실험 결과는 지우지 않으며,
        다음 라운드부터 입력칸에서도 빠집니다. 이 설정은 다음 run 에 이어집니다.
      </p>
      <div className="mt-1.5 flex flex-wrap items-center gap-1">
        <span className="mr-1 text-[10px] text-text-secondary">시점(분)</span>
        {grid.time_points.filter((t) => Number(t) > 0).map((t) => (
          <Chip key={`t${t}`} label={String(t)} on={times.has(Number(t))} onClick={() => toggleTime(t)}
                disabled={disabled} testId={`grid-exclusion-time-${t}`} />
        ))}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1">
        <span className="mr-1 text-[10px] text-text-secondary">용출액</span>
        {grid.solutions.map((s) => (
          <Chip key={`s${s}`} label={`pH ${s}`} on={sols.has(solutionKey(s))} onClick={() => toggleSol(s)}
                disabled={disabled} testId={`grid-exclusion-sol-${solutionKey(s)}`} />
        ))}
      </div>
      {problem && <p className="mt-1 text-[10px]" style={{ color: 'var(--color-warn)' }}>⚠ {problem}</p>}
    </div>
  )
}
